import React, { useState, useEffect } from 'react'
import { Card, CardBody, CardTitle } from 'reactstrap'
import Chart from 'react-apexcharts'
import styled from 'styled-components'
import { FormattedMessage, useIntl } from 'react-intl'
import _ from 'lodash'
import moment from 'moment'
import { LoadableCard } from './commons'
import { format, getISOWeek } from 'date-fns'
import { enUS, ptBR } from 'date-fns/locale'

import { queries } from '~/constants/mediaQuery'

const getOptions = custom => ({
  dataLabels: {
    enabled: false,
  },
  colors: custom?.colors,
  grid: {
    row: {
      colors: ['#f3f3f3', 'transparent'],
      opacity: 0.5,
    },
  },
  legend: {
    show: false,
  },
  fill: {
    type: 'solid',
    opacity: 0,
  },
  labels: custom?.labels,
  stroke: {
    curve: 'smooth',
    dashArray: custom?.dashArray,
  },
})

function MultiLineChartCard({
  options,
  series = [],
  growthPriority = 1,
  title,
  titleStyle,
  legacy = false,
  labels,
  loading,
  type = 'area',
  overrideOptions = false,
  ...props
}) {
  const [activeFilter, setActivefilter] = useState('day')
  const [customOptions, setCustomOptions] = useState(
    overrideOptions ? options : getOptions(options)
  )
  const intl = useIntl()
  const numberFormatter = new Intl.NumberFormat()

  const filterSeriesByDay = React.useCallback(
    locale => {
      const formatLocalizedDate = date => {
        const localizedHour = `${date}T12:00:00`
        return format(new Date(localizedHour), 'MMM d', {
          locale: locale === 'br' ? ptBR : enUS,
        })
      }

      const filteredByDay = series.map(serie => {
        const data = serie.data.map(item => {
          return { x: formatLocalizedDate(item.x), y: item.y }
        })

        return { ...serie, data }
      })

      return filteredByDay
    },
    [series]
  )

  const filterSeriesByWeek = React.useCallback(
    locale => {
      const weekName = item => {
        const d = new Date(item.x)
        const weekNumber = getISOWeek(d)
        const year = d.getFullYear()

        return locale === 'en'
          ? `${year} week ${weekNumber}`
          : `${year} sem ${weekNumber}`
      }

      const filteredByweek = series.map(serie => {
        const groupedByWeek = _.groupBy(serie.data, weekName)

        const data = []

        Object.keys(groupedByWeek).forEach(key => {
          const acc = groupedByWeek[key].reduce((sum, current) => {
            if (weekName(current).toString() === key) {
              return { x: key, y: sum.y + current.y }
            }
            return current
          })
          if (overrideOptions) acc.y = acc.y / groupedByWeek[key].length
          data.push(acc)
        })

        return { ...serie, data }
      })

      return filteredByweek
    },
    [series, overrideOptions]
  )

  const filterSeriesByMonth = React.useCallback(() => {
    const monthName = item => moment(item.x, 'YYYY-MM-DD').format('MMM')

    const filteredByMonth = series.map(serie => {
      const groupedByMonth = _.groupBy(serie.data, monthName)

      const data = []

      Object.keys(groupedByMonth).forEach(key => {
        const acc = groupedByMonth[key].reduce((sum, current) => {
          if (monthName(current) === key) {
            return { x: key, y: sum.y + current.y }
          }
          return current
        })
        if (overrideOptions) acc.y = acc.y / groupedByMonth[key].length
        data.push(acc)
      })

      return { ...serie, data }
    })

    return filteredByMonth
  }, [series, overrideOptions])

  const seriesData = React.useMemo(() => {
    if (!series?.length || !activeFilter) return []

    if (activeFilter === 'day') return filterSeriesByDay(intl.locale)
    if (activeFilter === 'week') return filterSeriesByWeek(intl.locale)
    if (activeFilter === 'month') return filterSeriesByMonth(intl.locale)
  }, [
    series,
    activeFilter,
    intl.locale,
    filterSeriesByDay,
    filterSeriesByWeek,
    filterSeriesByMonth,
  ])

  const classname = ({
    className = 'stats-card-body',
    hideChart,
    iconRight,
  }) => {
    return `d-flex  pt-2 ${className} ${
      !iconRight && !hideChart
        ? 'flex-column align-items-start'
        : iconRight
        ? 'justify-content-between flex-row-reverse align-items-center'
        : hideChart && !iconRight
        ? 'justify-content-center flex-column text-center'
        : null
    } ${!hideChart ? 'pb-0' : 'pb-2'}`
  }

  useEffect(() => {
    setCustomOptions(overrideOptions ? options : getOptions(options))
  }, [options, overrideOptions, setCustomOptions])

  return (
    <LoadableCard loading={loading}>
      <MultiLineChartCard.Container
        growthPriority={growthPriority}
        height={props.height}
        style={{ backgroundColor: legacy ? '#F9F9F9' : '#fff' }}
      >
        <CardBody className={classname(props)}>
          <div className="icon-section">
            <CardTitle
              style={{
                ...titleStyle,
              }}
            >
              <FormattedMessage id={title} />
              {legacy && (
                <div style={{ color: 'red' }}>
                  <FormattedMessage id="(Legacy)" />
                </div>
              )}
            </CardTitle>
            <ButtonsContainer>
              <FilterButtons
                isActive={activeFilter === 'day'}
                onClick={() => setActivefilter('day')}
              >
                <FormattedMessage id="day" />
              </FilterButtons>
              <FilterButtons
                isActive={activeFilter === 'week'}
                onClick={() => setActivefilter('week')}
              >
                <FormattedMessage id="week" />
              </FilterButtons>
              <FilterButtons
                isActive={activeFilter === 'month'}
                onClick={() => setActivefilter('month')}
              >
                <FormattedMessage id="month" />
              </FilterButtons>
            </ButtonsContainer>
          </div>
          <Stats className="title-section">
            {!labels &&
              seriesData?.map((serie, i) => (
                <div key={i}>
                  <small className="mb-0">
                    <FormattedMessage id={options?.labels?.[i] || '-'} />
                  </small>
                  <h2 style={{ color: options?.colors?.[i] }}>
                    {numberFormatter.format(serie?.total) || '--'}
                  </h2>
                </div>
              ))}
            {!!labels &&
              labels?.map((label, i) => (
                <div key={i}>
                  <small className="mb-0">
                    <FormattedMessage id={label.text} />
                  </small>
                  <h2 style={{ color: label.color }}>
                    {numberFormatter.format(label.data) || '--'}
                  </h2>
                </div>
              ))}
          </Stats>
        </CardBody>
        {!props.hideChart && (
          <ApexChart
            options={customOptions}
            series={seriesData}
            type={type}
            height={220}
          />
        )}
      </MultiLineChartCard.Container>
    </LoadableCard>
  )
}

const ApexChart = styled(Chart)`
  .apexcharts-toolbar {
    z-index: 0 !important;
  }
`

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  font-size: 1rem;

  .apexcharts-toolbar {
    z-index: 0 !important;
  }
`

const FilterButtons = styled.button`
  border: none;
  background: none;
  text-decoration: none;
  cursor: pointer;
  color: ${props => (props.isActive ? '#0091FF' : '#D2D2FF')};
`

const Stats = styled.div`
  min-height: 50px;
  display: flex;
  flex-direction: row;
  margin-top: 10px;
  gap: 20px;

  h2 {
    font-size: 24;
  }
`

const FilteredCard = ({ growthPriority: _, ...props }) => <Card {...props} />
MultiLineChartCard.Container = styled(FilteredCard)`
  flex: ${props => props.growthPriority} ${props => props.growthPriority} 300px;
  margin: 0 !important;
  height: ${props => props.height}px;

  @media (max-width: 900px) {
    flex: 1 1 300px;
  }

  .icon-section {
    width: 100%;
    display: flex;
    justify-content: space-between;

    @media (${queries.less.mdxs}) {
      flex-direction: column;
    }
  }
`

export default MultiLineChartCard
